import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, getFinancialYear } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Wallet, Landmark, Target, AlertTriangle } from 'lucide-react';
import { FinancialHealthScore } from '@/components/FinancialHealthScore';
import { AchievementBadges } from '@/components/AchievementBadges';
import { WeeklyDigest } from '@/components/WeeklyDigest';
import { SubscriptionDetector } from '@/components/SubscriptionDetector';
import { TaxDeductionMaximiser } from '@/components/TaxDeductionMaximiser';
import { PaydayCountdown } from '@/components/PaydayCountdown';
import { CashFlowForecast } from '@/components/CashFlowForecast';
import { YearInReviewWidget } from '@/components/YearInReviewWidget';
import { SmartNotifications } from '@/components/SmartNotifications';
import { Progress } from '@/components/ui/progress';
import { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useBasiqSync } from '@/hooks/use-basiq-sync';
import { supabase } from '@/integrations/supabase/client';

function useDisplayName() {
  const [displayName, setDisplayName] = useState('');
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase.from('profiles').select('display_name').eq('user_id', user.id).maybeSingle()
        .then(({ data }) => setDisplayName((data as any)?.display_name ?? ''));
    });
  }, []);
  return displayName;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function Dashboard() {
  const { data } = useAuth();
  const navigate = useNavigate();
  const { connections, basiqUserId, checkStatus } = useBasiqSync();
  const displayName = useDisplayName();
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const expiredConnections = useMemo(() => 
    connections.filter(c => c.status !== 'active'), [connections]);

  // Auto-check status on mount if user has basiq
  useMemo(() => {
    if (basiqUserId) checkStatus();
  }, [basiqUserId]);

  const monthlyStats = useMemo(() => {
    const monthTxns = data.transactions.filter(t => t.date.startsWith(currentMonth));
    const income = monthTxns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const expenses = monthTxns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    return { income, expenses, net: income - expenses };
  }, [data.transactions, currentMonth]);

  const upcomingBills = useMemo(() => {
    return data.bills
      .filter(b => !b.paid)
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
      .slice(0, 3);
  }, [data.bills]);

  const netWorth = useMemo(() => {
    const balances: Record<string, number> = {};
    for (const acc of (data.accounts ?? [])) {
      balances[acc.id] = acc.initialBalance;
    }
    for (const t of data.transactions) {
      if (t.accountId && balances[t.accountId] !== undefined) {
        balances[t.accountId] += t.type === 'income' ? t.amount : -t.amount;
      }
    }
    for (const tr of (data.transfers ?? [])) {
      if (balances[tr.fromAccountId] !== undefined) balances[tr.fromAccountId] -= tr.amount;
      if (balances[tr.toAccountId] !== undefined) balances[tr.toAccountId] += tr.amount;
    }
    return Object.values(balances).reduce((s, b) => s + b, 0);
  }, [data.accounts, data.transactions, data.transfers]);

  const hasAccounts = (data.accounts ?? []).length > 0;

  return (
    <div className="px-4 pt-6">
      <div className="mb-6">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {getGreeting()}{displayName ? `, ${displayName}` : ''}
        </p>
        <h1 className="font-display text-2xl font-bold text-foreground">Dashboard</h1>
        <p className="text-sm text-muted-foreground">FY {getFinancialYear(now)} · {now.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}</p>
      </div>


      {/* Expired bank connection warning */}
      {expiredConnections.length > 0 && (
        <Card className="mb-3 border-destructive/50 bg-destructive/5 cursor-pointer hover:bg-destructive/10 transition-colors" onClick={() => navigate('/more/bank-sync')}>
          <CardContent className="flex items-center gap-3 p-3">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-destructive">
                {expiredConnections.length === 1
                  ? `${expiredConnections[0].institution} connection expired`
                  : `${expiredConnections.length} bank connections expired`}
              </p>
              <p className="text-xs text-muted-foreground">Tap to reconnect and resume syncing</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3">
        {/* Net Worth card */}
        {hasAccounts && (
          <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => navigate('/accounts')}>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Landmark className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Net Worth</p>
                <p className={cn('font-display text-xl font-bold', netWorth < 0 && 'text-destructive')}>{formatAUD(netWorth)}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-success/10">
              <TrendingUp className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Income</p>
              <p className="font-display text-xl font-bold">{formatAUD(monthlyStats.income)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
              <TrendingDown className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Expenses</p>
              <p className="font-display text-xl font-bold">{formatAUD(monthlyStats.expenses)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <Wallet className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Net Balance (this month)</p>
              <p className="font-display text-xl font-bold">{formatAUD(monthlyStats.net)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Smart Alerts */}
      <div className="mt-6">
        <SmartNotifications />
      </div>

      {/* Engagement Widgets */}
      <div className="mt-6 grid gap-3">
        <PaydayCountdown />
        <CashFlowForecast />
        <WeeklyDigest />
        <SubscriptionDetector />
        <TaxDeductionMaximiser />
        <FinancialHealthScore />
        <AchievementBadges />
        <YearInReviewWidget />
      </div>

      {/* Upcoming Bills */}
      <div className="mt-6">
        <h2 className="mb-3 font-display text-lg font-semibold">Upcoming Bills</h2>
        {upcomingBills.length === 0 ? (
          <p className="text-sm text-muted-foreground">No upcoming bills</p>
        ) : (
          <div className="space-y-2">
            {upcomingBills.map(bill => (
              <Card key={bill.id}>
                <CardContent className="flex items-center justify-between p-3">
                  <div>
                    <p className="font-medium">{bill.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Due {new Date(bill.dueDate).toLocaleDateString('en-AU')}
                    </p>
                  </div>
                  <p className="font-display font-semibold">{formatAUD(bill.amount)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Savings Goals */}
      {(() => {
        const goals = (data.savingsGoals ?? []).filter(g => g.currentAmount < g.targetAmount).slice(0, 3);
        if (goals.length === 0) return null;
        return (
          <div className="mt-6">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">Savings Goals</h2>
              <button className="text-xs text-primary" onClick={() => navigate('/more/savings')}>View all</button>
            </div>
            <div className="space-y-2">
              {goals.map(g => {
                const pct = g.targetAmount > 0 ? (g.currentAmount / g.targetAmount) * 100 : 0;
                return (
                  <Card key={g.id} className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => navigate('/more/savings')}>
                    <CardContent className="p-3">
                      <div className="mb-1.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-primary" />
                          <p className="text-sm font-medium">{g.name}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">{Math.round(pct)}%</p>
                      </div>
                      <Progress value={Math.min(pct, 100)} className="h-1.5" />
                      <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                        <span>{formatAUD(g.currentAmount)}</span>
                        <span>{formatAUD(g.targetAmount)}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
